<template>
  <model-edition-form
    v-model="team"
    :model-name="t('team.title')"
    base-url="/api/teams"
    :model-id="props.modelId"
    :mode="props.mode"
    :with-text="false"
    :includes="['roles']"
    @model:loaded="orderRoles"
    @save:failed="openErroredTab"
  >
    <v-tabs
      v-model="tab"
      class="ms-4 mt-2"
    >
      <smpw-tab
        value="info"
        :has-errors="erroredTabs.includes('info')"
        :active="tab === 'info'"
        :text="t('team.title')"
      />
      <smpw-tab
        value="members"
        :has-errors="erroredTabs.includes('members')"
        :active="tab === 'members'"
        :text="t('role.member', 2)"
      />
    </v-tabs>

    <v-window
      v-model="tab"
      :touch="false"
    >
      <v-window-item value="info">
        <v-container>
          <v-text-field
            v-model="team.name"
            :disabled="!can('manage_teams')"
            :label="t('global.name')"
            v-bind="field('name')"
          />
          <user-select
            v-if="can('manage_teams')"
            v-model="team.overseer_user_id"
            :label="t('team.overseer')"
            v-bind="field('overseer_user_id', 'disabled')"
            active-only
          />

          <v-text-field
            v-if="can('manage_teams')"
            v-model="team.overseer_title"
            :label="t('team.overseer_title')"
            v-bind="field('overseer_title')"
          />

          <rich-text-editor

            v-model="team.description"
            :label="t('global.description')"
            v-bind="field('description', 'disabled')"
          />

          <v-text-field
            v-if="can('manage_teams')"
            v-model="team.order"
            type="number"
            :label="t('location.order')"
            :hint="t('team.order_hint')"
            v-bind="field('order')"
            hide-details
            class="mb-3"
          />

          <v-checkbox
            v-model="team.description_visible"
            :label="t('team.allow_all_members_hint')"
            class="text-font"
            v-bind="field('description_visible')"
          />

          <model-check-box
            v-if="can('manage_teams')"
            v-model="team.selected_roles"
            index-route="/api/roles"
            :label="t('team.role_label_category')"
            :text-key="[
              ['caption', ' - '],
              ['description', ''],
            ]"
          />
        </v-container>
      </v-window-item>
      <v-window-item value="members">
        <team-member-selector v-model="team.members" />
      </v-window-item>
    </v-window>
  </model-edition-form>
</template>

<script setup lang="ts">
import type { ModelEditionFormProps } from '@/composables';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import RichTextEditor from '@/components/rich-text/RichTextEditor.vue';
import { useModelEditionForm, usePermissions, useTabErrors } from '@/composables';
import { useAdminStore } from '@/stores/admin';
import ModelCheckBox from '../crud/ModelCheckBox.vue';
import ModelEditionForm from '../ModelEditionForm.vue';
import UserSelect from '../users/UserSelect.vue';
import TeamMemberSelector from './TeamMemberSelector.vue';
import { teamTabFields } from './teamTabFields';

const props = defineProps<ModelEditionFormProps>();
const { t } = useI18n();
const { field } = useModelEditionForm(props);
const tab = ref('info');
const { erroredTabs, openErroredTab } = useTabErrors(teamTabFields, tab);
const { can } = usePermissions();
const defaultItem = {
  id: null,
  name: '',
  description: '',
  overseer_title: '',
  overseer_user_id: null,
  members: [],
  roles: [],
  selected_roles: [],
  order: 0,
  description_visible: false,
  created_at: null,
  created_by: null,
  updated_at: null,
  updated_by: null,
};

/** A team being edited; a new one has no id and no overseer chosen yet. */
type DraftTeam = Omit<Partial<App.Models.Team>, 'id' | 'overseer_user_id'> & {
  id: number | null;
  overseer_user_id: number | null;
};

const team = ref<DraftTeam>(defaultItem);
function orderRoles() {
  team.value.selected_roles = (team.value.roles ?? []).reduce<number[]>(
    (previous, current) => {
      previous.push(current.id);
      return previous;
    },
    [],
  );
}
const { loadAllUsers } = useAdminStore();

loadAllUsers([{
  column: 'active',
  operator: '=',
  value: 1,
}]);
</script>
